import React from "react";

// Comment bodies are user-authored Markdown rendered with marked-react. Raw HTML in the source is
// already inert — marked-react emits it as escaped text (React's text escaping), never as live
// markup — so the only injection surface is the URL of a [link](url) or ![image](url). marked-react
// renders those into a live `<a href>` / `<img src>` WITHOUT sanitizing the scheme, so
// `[click](javascript:alert(1))` would be a one-click XSS. This renderer (passed as MarkedReact's
// `renderer`) strips dangerous URL schemes and hardens external links (noopener/noreferrer/nofollow).

// Schemes that can execute script. ASCII control chars + whitespace + the C1 range are removed
// before the test so obfuscated variants ("java\tscript:", a leading NUL, etc.) can't smuggle a
// scheme past it — browsers ignore those characters when resolving a URL.
const STRIP_CHARS: RegExp = /[\s\p{Cc}\p{Cf}]/gu;

function dangerousScheme(url: string, schemes: RegExp): boolean {
  const cleaned: string = (url ?? "").replace(STRIP_CHARS, "").toLowerCase();
  return schemes.test(cleaned);
}

const LINK_BLOCK: RegExp = /^(?:javascript|data|vbscript|file|blob):/;
// Images may legitimately be inline `data:` URIs; only the script-executing schemes are blocked
// (and those can't run from an <img> anyway — this is belt-and-suspenders).
const IMAGE_BLOCK: RegExp = /^(?:javascript|vbscript):/;

// marked-react keys siblings itself for its built-in elements, but a custom renderer's output is
// used as-is, so we must supply keys to avoid React list-key warnings.
let keySeq: number = 0;

function safeLink(href: string, text: React.ReactNode): React.ReactElement {
  const key: string = `md-a-${(keySeq += 1)}`;
  if (dangerousScheme(href, LINK_BLOCK)) return <span key={key}>{text}</span>;
  if (/^https?:/i.test((href ?? "").trim())) {
    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">
        {text}
      </a>
    );
  }
  return (
    <a key={key} href={href}>
      {text}
    </a>
  );
}

function safeImage(src: string, alt: string): React.ReactElement {
  const key: string = `md-img-${(keySeq += 1)}`;
  if (dangerousScheme(src, IMAGE_BLOCK)) return <span key={key}>{alt}</span>;
  return <img key={key} src={src} alt={alt} />;
}

export const SAFE_MARKDOWN_RENDERER = { link: safeLink, image: safeImage };

// Brand-word casing: any case of "kuika" → "Kuika", any case of "jira" → "JIRA" (whole words only,
// and not when wrapped in quotes/backticks — 'kuika', "jira", `kuika` stay as written).
export function normalizeBrands(text: string): string {
  return text.replace(/(?<!['"`])\b(kuika|jira)\b(?!['"`])/gi, (_m: string, word: string): string =>
    word.toLowerCase() === "kuika" ? "Kuika" : "JIRA"
  );
}

// A bare issue key like "KUI-123": a project prefix (a letter then letters/digits) + "-" + a number.
// Only those whose prefix is a real project key — and not wrapped in quotes/backticks — become chips.
const ISSUE_KEY_RE: RegExp = /(?<!['"`])\b[A-Z][A-Z0-9]*-\d+\b(?!['"`])/g;

function linkifyIssueKeys(text: string, issueKeys: Set<string>, onIssueKey: (key: string) => void): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let last: number = 0;
  const re: RegExp = new RegExp(ISSUE_KEY_RE.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const token: string = match[0];
    const prefix: string = token.slice(0, token.lastIndexOf("-"));
    if (!issueKeys.has(prefix)) continue; // not a real project key — leave as prose
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <a
        key={`ik-${(keySeq += 1)}`}
        className="kuika-board__issue-chip"
        href="#"
        role="link"
        title={`Open ${token}`}
        onClick={(event: React.MouseEvent) => {
          event.preventDefault();
          onIssueKey(token);
        }}
      >
        {token}
      </a>
    );
    last = match.index + token.length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export interface MarkdownRendererOptions {
  // Known project keys; when present, bare issue keys (KUI-12) in text become clickable chips.
  issueKeys?: Set<string>;
  onIssueKey?: (key: string) => void;
}

// A marked-react renderer with the same URL hardening as SAFE_MARKDOWN_RENDERER, plus optional
// promotion of real issue keys in plain text to chips (JIRA-style).
export function createMarkdownRenderer(options: MarkdownRendererOptions): object {
  const issueKeys: Set<string> | undefined = options.issueKeys;
  const onIssueKey: ((key: string) => void) | undefined = options.onIssueKey;
  if (!issueKeys || issueKeys.size === 0 || !onIssueKey) return SAFE_MARKDOWN_RENDERER;
  return {
    link: safeLink,
    image: safeImage,
    text(text: string): React.ReactNode {
      if (typeof text !== "string") return text;
      return linkifyIssueKeys(normalizeBrands(text), issueKeys, onIssueKey);
    }
  };
}
